import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useGetPublicSetAllCardsQuery } from 'store/services/sets/publicSetsApi';
import { useGetSeoSetQuery } from 'store/services/seo/publicSeoApi';
import SeoTradeData from 'components/SeoTradeData/SeoTradeData';
import { trackEvent } from 'utils/analytics';
import { SetCardsTable } from './components/SetCardsTable';
import { SetCardsList } from './components/SetCardsList';
import { SetViewToggle, SetCardsView } from './components/SetViewToggle';
import { SetHero } from './components/SetHero';
import { SetDescription } from './components/SetDescription';
import { RarityBreakdown, computeRarityCounts } from './components/RarityBreakdown';
import { SetItemListSchema } from './components/SetItemListSchema';
import { NotableCardsGrid } from './components/NotableCardsGrid';
import { SetStatsStrip } from './components/SetStatsStrip';
import { RelatedSets } from './components/RelatedSets';
import './SetDetailPage.css';

const DEFAULT_BANNER =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/og-images/sets-directory.jpg';

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'Unknown';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'Unknown';
  }
};

const formatSetType = (type: string | null): string => {
  if (!type) return '';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const SetDetailPage: React.FC = () => {
  const { setCode: rawSetCode } = useParams<{ setCode: string }>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const setCode = rawSetCode?.toLowerCase() || '';

  // Card display mode, deep-linkable via ?view=list. Unknown values fall back
  // to the table. Both views stay mounted in the DOM regardless (SEO).
  const [searchParams, setSearchParams] = useSearchParams();
  const cardsView: SetCardsView = searchParams.get('view') === 'list' ? 'list' : 'table';

  const handleViewChange = (next: SetCardsView) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'list') {
      nextParams.set('view', 'list');
    } else {
      nextParams.delete('view');
    }
    setSearchParams(nextParams, { replace: true });
    trackEvent('set_view_toggled', { setCode, view: next });
  };

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (rawSetCode && rawSetCode !== setCode) {
      navigate(`/sets/${setCode}`, { replace: true });
    }
  }, [rawSetCode, setCode, navigate]);

  const { data, isLoading, error } = useGetPublicSetAllCardsQuery(
    setCode,
    { skip: !setCode }
  );
  // Nightly trade-data snapshot (Asana 1219287567650303). A failure here (no
  // snapshot yet, or 404 for an excluded set) only hides the block; the page
  // renders as before. Title and noindex rule are unchanged (Eric's ruling).
  const { data: seoData } = useGetSeoSetQuery(setCode, { skip: !setCode });

  useEffect(() => {
    if (setCode) {
      trackEvent('set_viewed', { setCode });
    }
  }, [setCode]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  const set = data?.set;
  const cards = useMemo(() => data?.cards || [], [data?.cards]);
  const totalCards = data?.totalCards || set?.cardCount || 0;

  const rarityCounts = useMemo(() => computeRarityCounts(cards), [cards]);

  const firstCardImage = useMemo(() => {
    if (cards.length > 0 && cards[0].imageUris?.normal) {
      return cards[0].imageUris.normal;
    }
    return null;
  }, [cards]);

  const canonicalUrl = `https://trademagic.gg/sets/${setCode}`;
  const ogImage = set?.iconUrl || firstCardImage || DEFAULT_BANNER;

  const pageTitle = set
    ? `${set.name} (${set.code.toUpperCase()}) - All ${totalCards.toLocaleString()} Cards | TradeMagic`
    : 'Set Details - TradeMagic';

  const pageDescription = useMemo(() => {
    if (!set) return 'Browse Magic: The Gathering set details on TradeMagic.';
    const dateStr = formatDate(set.releasedAt);
    const detailed =
      `Browse all ${totalCards.toLocaleString()} cards from ${set.name} (${set.code.toUpperCase()}), released ${dateStr}. ` +
      `Includes ${rarityCounts.mythic} mythics, ${rarityCounts.rare} rares, ${rarityCounts.uncommon} uncommons, and ${rarityCounts.common} commons. Find prices and trades on TradeMagic.`;
    if (detailed.length <= 300) return detailed;
    return `Browse all ${totalCards.toLocaleString()} cards from ${set.name} (${set.code.toUpperCase()}), released ${dateStr}. Find prices and trades on TradeMagic.`;
  }, [set, totalCards, rarityCounts]);

  const pageKeywords = set
    ? [
        set.name,
        `${set.name} cards`,
        set.code.toUpperCase(),
        formatSetType(set.setType),
        'MTG',
        'Magic: The Gathering',
        'TCG',
        'trade',
        'prices',
        'mythic',
        'rare',
        'uncommon',
        'common',
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const setCollectionSchema = set
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${set.name} Cards`,
        description: `Complete list of all cards from ${set.name}`,
        url: canonicalUrl,
        numberOfItems: totalCards,
        about: {
          '@type': 'Thing',
          name: set.name,
        },
      }
    : null;

  if (isLoading) {
    return (
      <div className="set-detail-layout">
        <div className="set-detail-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/sets', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="set-detail-main">
            <TopNavigationBar
              title="Loading..."
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
            <div className="set-detail-container">
              <div className="set-detail-loading">
                <div className="set-detail-spinner" />
                <p>Loading set details...</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="set-detail-layout">
        {/* Raw Helmet: no canonical on a URL that does not resolve. */}
        <Helmet>
          <title>Set Not Found - TradeMagic</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="set-detail-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/sets', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="set-detail-main">
            <TopNavigationBar
              title="Set Not Found"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
            <div className="set-detail-container">
              <div className="set-detail-error">
                <h2>Set Not Found</h2>
                <p>The set "{rawSetCode}" could not be found.</p>
                <Link to="/sets" className="set-detail-back-link">
                  ← Back to all sets
                </Link>
              </div>
            </div>
          </div>
        </div>
        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOHelmet
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        image={ogImage}
        keywords={pageKeywords}
        type="website"
        // A set that resolves but holds zero cards is thin (GSC soft-404
        // ticket, decision 5). The sitemap already excludes it at generation.
        noindex={!!set && totalCards === 0}
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Sets', url: 'https://trademagic.gg/sets' },
          ...(set ? [{ name: set.name, url: canonicalUrl }] : []),
        ]}
      />

      {setCollectionSchema && <StructuredData data={setCollectionSchema} />}

      {set && cards.length > 0 && (
        <SetItemListSchema cards={cards} setCode={setCode} setName={set.name} />
      )}

      <div className="set-detail-layout">
        {isMobile && isMobileMenuOpen && (
          <div
            className="set-detail-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="set-detail-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/sets', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="set-detail-main">
            <TopNavigationBar
              title={set?.name || 'Set Details'}
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="set-detail-container">
              {set && (
                <>
                  <SetHero set={set} totalCards={totalCards} />

                  <SetStatsStrip setCode={setCode} />

                  <SetDescription set={set} totalCards={totalCards} />

                  {cards.length > 0 && (
                    <RarityBreakdown cards={cards} totalCards={totalCards} />
                  )}

                  {cards.length > 0 && (
                    <NotableCardsGrid cards={cards} setCode={setCode} />
                  )}

                  {seoData && (
                    <SeoTradeData
                      name={seoData.name || set.name}
                      stats={seoData.stats}
                      computedAt={seoData.computedAt}
                      mostWanted={seoData.mostWanted}
                      mostTraded={seoData.mostTraded}
                      mostAvailable={seoData.mostAvailable}
                      pageUrl={canonicalUrl}
                    />
                  )}

                  <section className="set-detail-cards-section">
                    <div className="set-detail-cards-header">
                      <h2 className="set-detail-cards-title">
                        All {totalCards.toLocaleString()} Cards
                      </h2>
                      <SetViewToggle view={cardsView} onChange={handleViewChange} />
                    </div>

                    {/* Both views stay mounted; the inactive one is hidden with
                        CSS only. Never gate either behind a JS conditional -
                        crawlers must see both subtrees in the rendered DOM. */}
                    <div className={cardsView === 'table' ? '' : 'set-cards-view--hidden'}>
                      <SetCardsTable cards={cards} setCode={setCode} />
                    </div>
                    <div className={cardsView === 'list' ? '' : 'set-cards-view--hidden'}>
                      <SetCardsList cards={cards} setCode={setCode} />
                    </div>
                  </section>

                  <RelatedSets setCode={setCode} />
                </>
              )}
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default SetDetailPage;
